import React, { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HStack,
  Icon,
  LoadingIcon,
  NewButton,
  P,
  VStack,
} from '@eluve/components';
import { AllMedicalCodeTypes, UserFileTypesLookup } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useUploadUserFile } from '@eluve/smart-blocks';

// Status types
type UploadStatus =
  | 'idle'
  | 'selecting'
  | 'uploading'
  | 'processing'
  | 'success'
  | 'error';

// Results of file processing
type ProcessingResults = {
  processed: number;
  skipped: number;
  errors: string[];
} | null;

// Dialog component for upload status
const UploadingDialog: React.FC<{
  status: UploadStatus;
  isOpen: boolean;
  onClose: () => void;
  results: ProcessingResults;
  inputRef?: React.RefObject<HTMLInputElement>;
}> = ({ status, isOpen, onClose, results, inputRef }) => {
  const handleFileSelect = () => {
    if (inputRef?.current) {
      inputRef.current.click();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Medical Codes</DialogTitle>
        </DialogHeader>
        <VStack align="center" justify="center" className="py-6" gap={4}>
          {status === 'selecting' && (
            <VStack align="center" wFull className="text-center">
              <P className="mb-4">
                Please select a CSV file with medical codes to upload. The file
                should include the following columns:
              </P>
              <ul className="mb-4 list-disc space-y-1 pl-6 text-left text-sm">
                <li>
                  <strong>Code</strong> - The medical code (required)
                </li>
                <li>
                  <strong>Type</strong> - Code type, should be one of:{' '}
                  {AllMedicalCodeTypes.join(', ')} (required)
                </li>
                <li>
                  <strong>Description</strong> - Description for the code
                  (optional)
                </li>
                <li>
                  <strong>Price</strong> - Price per unit in dollars e.g. '60'
                  or '120.50' (optional)
                </li>
                <li>
                  <strong>Modifiers</strong> - Comma-separated list of modifiers
                  (optional)
                </li>
              </ul>
              <P className="mb-4 font-medium text-text-negative">
                Warning: Uploading a new file will replace ALL existing code
                configurations for this group.
              </P>
              <NewButton text="Select File" onClick={handleFileSelect} />
              <P className="text-xs text-text-disabled">
                Maximum file size: 5MB, CSV format only
              </P>
            </VStack>
          )}
          {status === 'uploading' && (
            <>
              <LoadingIcon
                size="lg"
                className="mb-4 text-bg-accent-strong-primary"
              />
              <P>Uploading your file...</P>
            </>
          )}
          {status === 'processing' && (
            <>
              <LoadingIcon
                size="lg"
                className="mb-4 text-bg-accent-strong-primary"
              />
              <P>Processing medical codes, this may take a moment...</P>
            </>
          )}
          {status === 'success' && results && (
            <VStack wFull align="center">
              <div className="mb-4 flex items-center justify-center">
                <Icon
                  name="CheckCircle2"
                  size="lg"
                  className="text-text-positive"
                />
              </div>
              <P className="mb-4 text-center">Upload complete!</P>
              <VStack wFull className="text-sm">
                <HStack wFull justify="between">
                  <span>Successfully processed:</span>
                  <span className="font-medium">{results.processed} codes</span>
                </HStack>
                <HStack wFull justify="between">
                  <span>Skipped:</span>
                  <span className="font-medium">{results.skipped} codes</span>
                </HStack>
                {results.errors.length > 0 && (
                  <div>
                    <P className="mb-1 mt-2 font-medium">Errors:</P>
                    <div className="max-h-32 overflow-y-auto  rounded p-2 text-xs">
                      {results.errors.slice(0, 5).map((error, i) => (
                        <div key={i} className="mb-1">
                          {error}
                        </div>
                      ))}
                      {results.errors.length > 5 && (
                        <div className="text-text-supporting">
                          ...and {results.errors.length - 5} more errors
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </VStack>
            </VStack>
          )}
          {status === 'error' && (
            <>
              <Icon
                name="AlertCircle"
                size="lg"
                className="mb-4 text-text-negative"
              />
              <P className="mb-4 text-center">Failed to upload medical codes</P>
              {results?.errors && results.errors.length > 0 && (
                <VStack gap={2} className="text-xs text-text-negative">
                  {results.errors.slice(0, 5).map((error, i) => (
                    <div key={i}>{error}</div>
                  ))}
                  {results.errors.length > 5 && (
                    <div className="text-text-supporting">
                      ...and {results.errors.length - 5} more errors
                    </div>
                  )}
                </VStack>
              )}
            </>
          )}
        </VStack>
      </DialogContent>
    </Dialog>
  );
};

export const UploadCodesButton: React.FC<{ onSuccess?: () => void }> = ({
  onSuccess,
}) => {
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };
  const logger = useNamedLogger('UploadCodesButton');

  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [processingResults, setProcessingResults] =
    useState<ProcessingResults>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const apiClient = useApiClient();
  const { uploadUserFileToGCS } = useUploadUserFile(tenantId);

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file) return;

      // Check if file is CSV
      if (!file.name.toLowerCase().endsWith('.csv')) {
        logger.error('File must be a CSV file');
        setUploadStatus('error');
        return;
      }

      try {
        // Update status to uploading
        setUploadStatus('uploading');

        const fileExt = file.name.split('.').pop() ?? 'csv';

        const userUploadId = await uploadUserFileToGCS({
          file,
          mimeType: 'text/csv',
          extension: fileExt,
          fileType: UserFileTypesLookup.MEDICAL_CODE_CONFIGS,
        });

        if (!userUploadId) {
          throw new Error('Failed to upload file');
        }

        setUploadStatus('processing');
        const processingResponse =
          await apiClient.medicalCodes.processMedicalCodesFile({
            params: { tenantId, userGroupId },
            body: {
              userUploadId,
            },
          });

        if (processingResponse.status !== 200) {
          throw new Error('Failed to process uploaded file');
        }

        setProcessingResults(processingResponse.body);

        if (!processingResponse.body.success) {
          setUploadStatus('error');
        } else {
          onSuccess?.();
          setUploadStatus('success');
        }
      } catch (error) {
        logger.error('Error uploading medical codes:', { error });
        setUploadStatus('error');
      }
    },
    [apiClient, tenantId, userGroupId, logger, uploadUserFileToGCS],
  );

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }

      if (e.target) {
        e.target.value = '';
      }
    },
    [handleFileUpload],
  );

  // Start upload process
  const startUpload = useCallback(() => {
    setProcessingResults(null);
    setUploadStatus('selecting');
    setShowUploadDialog(true);
  }, []);

  const handleCloseUploadDialog = useCallback(() => {
    setShowUploadDialog(false);
    setUploadStatus('idle');
  }, []);

  return (
    <>
      <NewButton text="Upload Codes" onClick={startUpload} />

      {/* Hidden file input for CSV upload */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* Upload Status Dialog */}
      <UploadingDialog
        status={uploadStatus}
        isOpen={showUploadDialog}
        onClose={handleCloseUploadDialog}
        results={processingResults}
        inputRef={fileInputRef}
      />
    </>
  );
};
